import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { debounce } from 'es-toolkit';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { InputAdornment, Stack, TextField } from '@mui/material';
import { Description, Search } from '@mui/icons-material';

import { timeZoneAtom } from '@/interfaces/utils';
import { compareInLowerCase } from '@/components/common/utils';

import type { ChangeEvent } from 'react';
import type { GridColDef } from '@mui/x-data-grid';

interface GridRow {
  id: number;
  name: string;
  serialNumber: string;
  orderNumber: string;
  createdOn: string;
  totalAmount: number;
}

// FIXME: Remove after API integration
const sampleRows = [
  {
    id: 0,
    name: 'Device 1',
    serialNumber: 'm005-0001-0001',
    orderNumber: '94473950294353205883184504134776',
    createdOn: '2025-02-29T12:50:00.000+0900',
    totalAmount: 45000,
  },
  {
    id: 1,
    name: 'Device 2',
    serialNumber: 'm003-0002-0005',
    orderNumber: '81365700539167535990647436786134',
    createdOn: '2024-08-13T08:23:21.000+0900',
    totalAmount: 18000,
  },
];

export default function SubscriptionHistory() {
  const timeZone = useAtomValue(timeZoneAtom);

  const [query, setQuery] = useState('');
  // FIXME: Fetch rows from API
  const [rows, setRows] = useState<GridRow[]>(sampleRows);
  const { t, i18n } = useTranslation();

  const isKorean = i18n.language.startsWith('ko');
  const locale = isKorean ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const timeFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'orderNumber', headerName: t('Settings.label.orderNumber'), flex: 2, sortable: false },
      { field: 'name', headerName: t('Common.label.name'), flex: 1, sortable: false },
      { field: 'serialNumber', headerName: t('Devices.label.serialNumber'), flex: 1, sortable: false },
      {
        field: 'createdOn',
        type: 'dateTime',
        headerName: t('Common.label.createdOn'),
        flex: 1,
        sortable: true,
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
      {
        field: 'totalAmount',
        headerName: t('Settings.label.totalAmount'),
        flex: 1,
        sortable: false,
        valueFormatter: (value: number) => value.toLocaleString(),
      },
      {
        field: 'receipt',
        type: 'actions',
        headerName: t('Settings.label.receipt'),
        flex: 1,
        sortable: false,
        getActions: () => [<GridActionsCellItem icon={<Description />} label="description" />],
      },
    ],
    [t, timeFormat, timeZone],
  );

  const filterDevices = useMemo(
    () =>
      debounce((query: string) => {
        // FIXME: Use data from API
        if (query === '') {
          setRows(sampleRows);
        } else {
          setRows(
            sampleRows.filter(
              (row) =>
                compareInLowerCase(row.serialNumber, query) ||
                compareInLowerCase(row.orderNumber, query) ||
                compareInLowerCase(row.name, query),
            ),
          );
        }
      }, 500),
    [],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterDevices(value);
  }

  return (
    <>
      <Stack gap={2} sx={{ flexGrow: 1, overflow: 'auto', pt: 2 }}>
        <TextField
          placeholder={t('Common.label.search')}
          value={query}
          onChange={onQueryChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: '300px', mr: 'auto', bgcolor: 'white' }}
        />

        <DataGrid
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>
    </>
  );
}
